import React, { useState, useEffect } from "react";
import { addDestination } from "../../apis/firebase";
import { Link } from "react-router-dom";

import { DestinationType, LINKED_CLIENT_ID, USHER } from "../../constants";
import { addUserDestination } from "../../state/User";
import { getParameter, verifyRealUser } from "../helpers";
import Alert from "../utils/icons/Alert";
import LoaderIcon from "../utils/icons/LoaderIcon";
import { closePopUp, showLoadingPopUp } from "../utils/Overlay";
import Check from "../utils/icons/Check";

const stateUID = `contempas_tobenin`;

export function AddLinkedInConnection({
  onError,
  user,
}: {
  onError: Function;
  user: User;
}) {
  const [inProgress, setProgressFlag] = useState(false);
  const redirect = encodeURIComponent(
    window.location.href.split("?")[0] + "?integration=linkedin"
  );
  const params = {
    response_type: "code",
    client_id: LINKED_CLIENT_ID,
    redirect_uri: redirect,
    state: stateUID,
    scope: encodeURIComponent("w_member_social r_liteprofile"),
  };
  const url = inProgress
    ? "#"
    : `https://www.linkedin.com/oauth/v2/authorization?${Object.keys(params)
        .map((key) => `${key}=${params[key]}`)
        .join("&")}`;

  useEffect(() => {
    const code = getParameter("code");
    const integration = getParameter("integration");
    const state = getParameter("state");
    const error = getParameter("error");

    const fail = () => {
      onError(["Error connecting to LinkedIn.", "/help/linkedin"]);
      setProgressFlag(false);
      closePopUp();
    };

    if (code && integration === "linkedin" && state === stateUID) {
      setProgressFlag(true);
      showLoadingPopUp("Linking your LinkedIn account. Please wait.");
      async function getLinkedinToken() {
        try {
          const res = await fetch(`/api/li?c=${code}&r=${redirect}`);
          const data = await res.json();
          if (res.status === 200) {
            const newDestination = await addDestination(
              user,
              DestinationType.LINKEDIN,
              data,
              {
                name: data.name,
              }
            );
            addUserDestination(newDestination);
            closePopUp();
          } else {
            fail();
          }
        } catch (err) {
          console.log(err);
          fail();
        }
      }
      getLinkedinToken();
    } else if (error) {
      fail();
    }
  }, []);

  if (user.demo) {
    return (
      <button
        onClick={() => verifyRealUser(user, () => {})}
        className="secondary with-icon fz08"
      >
        <span className="vam">Link a LinkedIn account</span>
      </button>
    );
  }

  return (
    <a
      href={url}
      className="secondary with-icon fz08"
      data-disabled={inProgress}
    >
      {inProgress ? "⏳ " : null}
      <span className="vam">Link a LinkedIn account</span>
    </a>
  );
}

export function getLinkedInTokenExpiry(destination: Destination) {
  const [isValid, setValidFlag] = useState(null);
  const [UI, setUI] = useState(
    <small className="op08">
      (<LoaderIcon size={14} /> Verifying destination ...)
    </small>
  );

  useEffect(() => {
    async function getTokenInfo() {
      try {
        const res = await fetch(USHER, {
          method: "POST",
          body: JSON.stringify({
            operation: "linkedin_token_verification",
            id: destination.id,
          }),
          mode: "cors",
          headers: {
            "Content-Type": "application/json",
          },
        });
        if (res.status === 200) {
          const { expires_at, created_at } = await res.json();
          const diffTime = expires_at * 1000 - new Date().getTime();
          const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
          if (diffDays < 0) {
            setValidFlag(false);
            setUI(
              <small className="op06">
                (<Alert size={14} /> The access token for this destination
                expired. Please re-create it in the{" "}
                <Link to="/destinations">Destinations</Link> section.)
              </small>
            );
          } else {
            setValidFlag(true);
            setUI(
              <small className="op06">(Expires after {diffDays} days.)</small>
            );
          }
        } else {
          console.log(res.status);
          setUI(null);
        }
      } catch (err) {
        console.log(err);
        setUI(null);
      }
    }
    if (destination.type === DestinationType.LINKEDIN) {
      getTokenInfo();
    }
  }, [destination.id]);

  return [isValid, UI];
}
